import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import { formatDateDDMMYYEmpty } from "../../../components/common/dateUtils";
import logoo from "../../../assets/logo-kribhcoAgri.png";

const SaleOrderPreview = () => {
  const { saleId } = useParams();
  const [saleOrder, setSaleOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const fetchSaleOrder = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/sales/sales-order-get/${saleId}`,
          {
            credentials: "include",
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSaleOrder(data.data);
        } else {
          setError("Failed to fetch sale order details");
        }
      } catch (err) {
        setError("Error fetching sale order details");
      } finally {
        setLoading(false);
      }
    };

    if (saleId) {
      fetchSaleOrder();
    }
  }, [saleId]);

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === "") return "0.00";
    return parseFloat(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatQuantity = (num) => {
    if (num === null || num === undefined || num === "") return "0.000";
    return parseFloat(num).toLocaleString("en-IN", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Calculate required rows for address
  const calculateAddressRows = (address) => {
    if (!address) return 4; // default rows
    const lineCount = Math.ceil(address.length / 50); // ~50 chars per line
    return Math.min(8, Math.max(4, lineCount)); // Min 4 rows, max 8 rows
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <p className="text-red-500 text-center mt-4">{error}</p>;
  if (!saleOrder)
    return (
      <p className="text-red-500 text-center mt-4">No order data available</p>
    );

  const totalAmount =
    saleOrder.quantity_mt && saleOrder.sale_price_per_mt
      ? parseFloat(saleOrder.quantity_mt) *
        parseFloat(saleOrder.sale_price_per_mt)
      : 0;

  const addressRows = calculateAddressRows(saleOrder.domestic_buyer_address);
  const addressHeight = addressRows * 20; // 20px per row

  return (
    <>
      <style>
        {`
          .a4-container {
            width: 210mm;
            max-width: 100%;
            margin: 0 auto;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            padding: 10mm;
            overflow: hidden;
            position: relative;
          }
          @media print {
            @page {
              size: A4;
              margin: 0.2in;
            }
            .a4-container {
              box-shadow: none;
              margin: 0;
              padding: 5mm;
              width: 100%;
              max-width: none;
            }
            table {
              width: 100% !important;
              max-width: none !important;
              font-size: 11px !important;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      <div
        className="a4-container bg-white"
        ref={tableRef}
        id="printable-table"
      >
        <table
          className="table-auto border-collapse border bg-white border-black text-[14px] w-full"
          style={{ fontSize: "12px", maxWidth: "100%" }}
        >
          <thead className="bg-white">
            <tr>
              <th
                className="border border-black flex text-left p-0 m-0 font-normal"
                colSpan="5"
              >
                <div className="flex w-full">
                  {/* Left Column (KRIBHCO Details) */}
                  <div className="w-[250px]">
                    <div className="text-[13px] text-[#18a152] font-bold text-center border-r border-b border-black h-[150px] flex flex-col justify-center items-center p-1 overflow-hidden">
                      <p className="text-[14px] font-bold leading-tight mb-1">
                        {saleOrder.seller_business_name || "KRIBHCO Agri Business Ltd."}
                      </p>
                      <p className="font-semibold text-[10px] leading-tight mb-1" style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
                        {saleOrder.seller_address || "ADMINISTRATIVE BUILDING,\nKRISHAK BHARTI COOPERATIVE LIMITED,\nHAZIRA ROAD, LIMLA, SURAT, GUJARAT"}
                      </p>
                      <p className="font-semibold text-[11px] leading-tight mb-1">
                        GSTIN: {saleOrder.seller_gst || "24AAJCK3035F1ZU"}
                      </p>
                      <div className="mt-1">
                        <img
                          src={logoo}
                          alt="logo"
                          className="w-[150px] h-auto max-h-[25px] mx-auto object-contain"
                        />
                      </div>
                    </div>

                    <div className="border-t border-b border-black h-[22px]"></div>
                    <div className="border-t border-b border-r border-black text-[12px] h-[22px] flex items-end ">
                      &nbsp;Buyer Name:
                    </div>
                    <div
                      className="border border-black text-[12px]"
                      style={{
                        height: `${Math.max(60, addressHeight * 0.8)}px`,
                      }}
                    >
                      <div
                        className="break-words h-full"
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "break-word",
                        }}
                      >
                        &nbsp;Buyer Address:
                      </div>
                    </div>
                    <div className="border-r border-t border-black text-[12px] h-[22px] flex items-end">
                      &nbsp;Buyer Gst
                    </div>
                  </div>

                  {/* Right Column (Order Details) */}
                  <div className="flex-1">
                    <h2 className="font-semibold text-blue-900 flex items-center justify-center text-2xl border-l border-b border-black h-[75px]">
                      Sale Order
                    </h2>
                    <div className="flex">
                      {/* Main Details */}
                      <div className="flex-1">
                        <div className="text-[12px] font-bold text-center border border-black h-[75px]">
                          <p className="text-[12px] font-bold text-blue-900 h-[25px] flex items-end justify-center">
                            PAN No. {saleOrder.seller_pan || "AAJCK 3035 F"}
                          </p>
                          <p className="text-[12px] font-bold text-blue-900 h-[25px] flex items-center justify-center">
                            CIN No. {saleOrder.seller_cin || "U01110DL2022PLC393197"}
                          </p>
                          <p className="text-[12px] font-bold text-blue-900 h-[25px] flex items-baseline justify-center">
                            GST No. {saleOrder.seller_gst || "24AAJCK3035F1ZU"}
                          </p>
                        </div>
                        <div className="border-t border-b border-r border-black h-[22px]"></div>
                        <div className="border border-black text-[12px] h-[22px] flex items-center overflow-hidden">
                          &nbsp;{saleOrder.domestic_buyer_name || "-"}
                        </div>
                        <div
                          className="border-r border-t border-b border-black text-[12px] p-1"
                          style={{
                            height: `${Math.max(60, addressHeight * 0.8)}px`,
                          }}
                        >
                          <div
                            className="break-words"
                            style={{
                              whiteSpace: "pre-wrap",
                              overflowWrap: "break-word",
                              lineHeight: "1.1",
                            }}
                          >
                            {saleOrder.domestic_buyer_address || "-"}
                          </div>
                        </div>
                        <div className="border-r border-t border-l border-black text-[12px] h-[22px] flex items-center overflow-hidden">
                          &nbsp;{saleOrder.domestic_buyer_gst || "-"}
                        </div>
                      </div>

                      {/* Labels Column */}
                      <div className="flex">
                        {/* Labels Column - Added padding-right for spacing */}
                        <div className="text-[10px] font-bold text-center border-l border-t border-r border-black w-[100px]">
                          <p className="font-semibold text-start h-[26px] flex items-center pl-1 pr-1">
                            Date:
                          </p>
                          <p className="font-semibold text-start h-[26px] flex items-center pl-1 pr-1">
                            Invoice no:
                          </p>
                          <p className="font-semibold text-start h-[26px] flex items-center pl-1 pr-1">
                            Client Ref nr:
                          </p>
                          <p className="font-semibold text-start h-[26px] flex items-center pl-1 pr-1">
                            Sales Contract:
                          </p>
                          <p className="font-semibold text-start h-[26px] flex items-center pl-1 pr-1">
                            Broker.:
                          </p>
                          <p className="font-semibold text-start h-[26px] flex items-center pl-1 pr-1">
                            DO. No.:
                          </p>
                          <p className="font-semibold text-start h-[26px] flex items-center pl-1 pr-1">
                            Port Loading:
                          </p>
                        </div>

                        {/* Values Column - Adjusted padding and alignment */}
                        <div className="text-[10px] font-bold text-center border-l border-t border-black w-[130px]">
                          <p className="font-semibold h-[26px] flex items-center justify-end pr-2 pl-1">
                            {formatDateDDMMYYEmpty(saleOrder.order_date)}
                          </p>
                          <p className="font-semibold h-[26px] flex items-center justify-end pr-2 pl-1">
                            {saleOrder.invoice_no || "-"}
                          </p>
                          <p className="font-semibold h-[26px] flex items-center justify-end pr-2 pl-1">
                            -
                          </p>
                          <p className="font-semibold h-[26px] flex items-center justify-end pr-2 pl-1">
                            {saleOrder.sales_contract_no || "-"}
                          </p>
                          <p className="font-semibold h-[26px] flex items-center justify-end pr-2 pl-1">
                            {saleOrder.broker_name || "-"}
                          </p>
                          <p className="font-semibold h-[26px] flex items-center justify-end pr-2 pl-1">
                            {saleOrder.delivery_order_no || "-"}
                          </p>
                          <p className="font-semibold h-[26px] flex items-center justify-end pr-2 pl-1">
                            {saleOrder.port_of_loading || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </th>
            </tr>

            {/* Table Headers */}
            <tr className="flex w-full">
              <th className="border-t border-b pl-1 border-l border-r border-black w-[220px] h-[22px] flex text-[11px] bg-gray-200 items-center justify-start pr-2 font-bold">
                Particulars
              </th>
              <th className="border-t border-b border-l border-r border-black w-[150px] h-[22px] flex text-[11px] bg-gray-200 items-center justify-center pr-2 font-bold">
                Quantity (In Mtons)
              </th>
              <th className="border-t border-b border-l border-r border-black w-[150px] h-[22px] flex text-[11px] bg-gray-200 items-center justify-center pr-2 font-bold">
                Rate / Ton (Per Mt)
              </th>
              <th className="border-t border-b border-l border-r border-black w-[200px] h-[22px] flex text-[11px] bg-gray-200 items-center justify-end pr-2 font-bold">
                Amount in INR
              </th>
            </tr>
          </thead>

          <tbody>
            {/* Data Row for the sale order */}
            <tr className="flex text-[13px] w-full">
              <td className="border-l border-r pl-1 border-b border-black w-[220px] h-[24px] flex items-center justify-start pr-2 overflow-hidden">
                {saleOrder.commodity_name || "-"}
              </td>
              <td className="border-l border-r border-b border-black w-[150px] h-[24px] flex items-center justify-center pr-2 overflow-hidden">
                {formatQuantity(saleOrder.quantity_mt)}
              </td>
              <td className="border-l border-r border-b border-black w-[150px] h-[24px] flex items-center justify-center pr-2 overflow-hidden">
                ₹ {formatNumber(saleOrder.sale_price_per_mt)}
              </td>
              <td className="border-l border-r border-b border-black w-[200px] h-[24px] flex items-center justify-end pr-2 overflow-hidden">
                ₹ {formatNumber(totalAmount)}
              </td>
            </tr>

            {/* Fill remaining rows */}
            {[...Array(15)].map((_, i) => (
              <tr className="flex w-full" key={`empty-row-${i}`}>
                <td className="border-l border-r border-b border-black w-[220px] h-[24px]"></td>
                <td className="border-l border-r border-b border-black w-[150px] h-[24px]"></td>
                <td className="border-l border-r border-b border-black w-[150px] h-[24px]"></td>
                <td className="border-l border-r border-b border-black w-[200px] h-[24px]"></td>
              </tr>
            ))}

            {/* Bottom Section - Grid Layout */}
            <tr>
              <td
                colSpan="4"
                className="border-t border-b border-black h-[180px] p-0 m-0 bg-white"
              >
                <div className="grid grid-cols-3 w-full h-full">
                  {/* Left Column */}
                  <div className="border-r border-black flex flex-col">
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1"></div>
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1">
                      Vehicle No.
                    </div>
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1">
                      &nbsp;
                    </div>
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1">
                      &nbsp;
                    </div>
                    <div className="flex-1 text-[11px] flex items-center pl-1">
                      &nbsp;
                    </div>
                  </div>

                  {/* Middle Column */}
                  <div className="border-r border-black flex flex-col">
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1">
                      Total
                    </div>
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1">
                      {saleOrder.vehicle_no || "-"}
                    </div>
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1">
                      &nbsp;
                    </div>
                    <div className="border-b border-black h-[24px] text-[11px] flex items-center pl-1">
                      &nbsp;
                    </div>
                    <div className="flex-1 text-[11px] flex items-center pl-1">
                      &nbsp;
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="flex flex-col">
                    {/* Total Row */}
                    <div className="border-b border-black h-[24px] flex items-center">
                      <div className="flex-1 text-[11px] font-bold text-right pr-2"></div>
                      <div className="w-[100px] text-[11px] font-bold text-right pr-2">
                        ₹ {formatNumber(totalAmount)}
                      </div>
                    </div>
                    {/* Net Amount Row with Red Background */}
                    <div className="border-b border-black h-[24px] flex items-center bg-red-100">
                      <div className="flex-1 text-[11px] font-bold text-right pr-2">
                        Net Amount
                      </div>
                      <div className="w-[100px] text-[11px] font-bold text-right pr-2">
                        ₹ {formatNumber(totalAmount)}
                      </div>
                    </div>
                    {/* Signature section */}
                    <div className="flex-1 flex flex-col justify-center items-center">
                      <div className="text-[11px] font-bold text-center">
                        For {saleOrder.seller_business_name || "Kribhco Agri Business Limited"}
                      </div>
                     
                      {saleOrder.issuing_authority_signature && (
                        <img 
                          src={`${API_BASE_URL}/uploads/${saleOrder.issuing_authority_signature}`} 
                          alt="Signature" 
                          className="h-14 w-auto mt-2 print:mt-4"
                          style={{height: '65px', maxWidth: '200px'}}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="text-[11px] font-bold text-center mt-2">
                        Authorised Signatory
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>

            {/* Terms and Conditions - First Section (White Background) */}
            <tr>
              <td
                colSpan="4"
                className="border-t border-black p-0 m-0 bg-white"
              >
                <div className="text-[10px] p-1">
                  <div className="font-semibold whitespace-nowrap overflow-hidden text-center">
                    Interest Payable @ 18% if payment delayed beyond 7 days from
                    date of loading / invoice
                  </div>
                </div>
              </td>
            </tr>

            {/* Terms and Conditions - Second Section (Yellow Background) */}
            <tr>
              <td
                colSpan="4"
                className="border-t border-black p-0 m-0 bg-yellow-100"
              >
                <div className="text-[10px] p-1">
                  <div className="font-semibold whitespace-nowrap overflow-hidden text-center">
                    All Trades Disputes / Claims subject to Sauda Letter and
                    restricted to Delhi
                  </div>
                </div>
              </td>
            </tr>

            {/* Terms and Conditions - Third Section (White Background) */}
            <tr>
              <td
                colSpan="4"
                className="border-t border-b border-black p-0 m-0 bg-white"
              >
                <div className="text-[10px] p-1">
                  <div className="font-semibold whitespace-nowrap overflow-hidden text-center">
                    Ex Warehouse - Quality & Quantity Final at the Time of
                    Loading & No Claim Acceptable once the truck departs the
                    warehouse
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mr-6 no-print">
        <button
          onClick={handlePrint}
          className="mt-3 p-1 px-6 bg-[#009E4D] text-white font-semibold rounded-[4px] hover:bg-green-600 text-sm"
        >
          Print
        </button>
      </div>
    </>
  );
};

export default SaleOrderPreview;
